/**
 * Microsoft Clarity — per-seller, opt-in via public API + consent. Fail-safe: never throws.
 */

let initializedProjectId = null;
let clarityReady = false;
const scriptPromises = new Map();

export function loadMicrosoftClarityScript(projectId) {
  if (typeof window === "undefined" || !projectId) {
    return Promise.resolve(false);
  }

  if (clarityReady && initializedProjectId === projectId) {
    return Promise.resolve(true);
  }

  if (scriptPromises.has(projectId)) {
    return scriptPromises.get(projectId);
  }

  const promise = new Promise((resolve) => {
    let settled = false;
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      if (!ok) scriptPromises.delete(projectId);
      clarityReady = ok;
      if (ok) initializedProjectId = projectId;
      resolve(ok);
    };

    try {
      const existing = document.querySelector(`script[src*="clarity.ms/tag/${projectId}"]`);
      if (existing) {
        finish(true);
        return;
      }

      window.clarity =
        window.clarity ||
        function clarityStub() {
          (window.clarity.q = window.clarity.q || []).push(arguments);
        };

      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.clarity.ms/tag/${encodeURIComponent(projectId)}`;
      s.onload = () => finish(true);
      s.onerror = () => finish(false);
      document.head.appendChild(s);
    } catch {
      finish(false);
    }
  });

  scriptPromises.set(projectId, promise);
  return promise;
}

export async function initMicrosoftClarity(projectId) {
  if (!projectId) return false;
  try {
    return await loadMicrosoftClarityScript(projectId);
  } catch {
    return false;
  }
}

export function resetMicrosoftClaritySession() {
  initializedProjectId = null;
  clarityReady = false;
  scriptPromises.clear();
}

export function isMicrosoftClarityReady() {
  return clarityReady && Boolean(initializedProjectId);
}

/** Custom Clarity event tag (heatmaps/recordings stay automatic). Fail-safe. */
export function trackClarityEvent(eventName) {
  try {
    if (!clarityReady || !eventName || typeof window === "undefined") return;
    if (typeof window.clarity === "function") {
      window.clarity("event", String(eventName));
    }
  } catch {
    /* ignore */
  }
}
